/* eslint-disable react/react-in-jsx-scope */
import React from 'react'
import { format } from 'date-fns'
import _ from 'lodash'
import { Button } from 'gdpp-components/dist/cjs/components/atoms'

import { getGlobalParams, getCustomIntl } from 'config/config'
import { getSpecificAttrValue, sortBySpecificArray, replaceSpecialChars, toFixedCustom, divideBy100 } from 'utils'

import NumberComponent from '../../atoms/Number'
import DateComponent from '../../atoms/Date'

import Tooltip from '../../atoms/Tooltip'
import ExternalLink from '../../atoms/ExternalLink'
import { fillTerms } from '../../../utils/merge'

const tcLanguageTag = 'zh-TW'
const enLanguageTag = 'en-001'
const valuePlaceholder = '-'
const numberConfig = { redIsRaise: true, signDisplay: 'always', style: 'percent' }
const ANNUAL_RETURE_YEARS = 10

const addPlaceholderValue = (value) => value || valuePlaceholder
const parseFloatWithFixed = ({ value, fractionDigits = 0, prefix = '', suffix = '' }) => {
  const parsedValue = parseFloat(value)
  const fixedValue = _.isNaN(parsedValue) ? '-' : `${prefix}${toFixedCustom(parsedValue, fractionDigits)}${suffix}`
  return fixedValue
}

const formatDate = (date, dateFormat = 'yyyy-MM-dd') => {
  return date !== '-' ? format(new Date(date), dateFormat) : date
}

const formatFundDetailOverview = (fundDetailOverviewData) => {
  const intl = getCustomIntl()
  const aumAmount = parseFloatWithFixed({
    value: getSpecificAttrValue({ originData: fundDetailOverviewData.aum, dataPath: 'fundAum' })
  })

  const formatManagersList = (managerList, type) => {
    const managersInfo = managerList.map((managerInfo, managerIndex) => {
      if (type === 'fullName') {
        const { fullName } = managerInfo
        const manangerNameZh = getSpecificAttrValue({ originData: fullName, attrValue: tcLanguageTag, valuePlaceholder: '' })
        const manangerNameEn = getSpecificAttrValue({ originData: fullName, attrValue: enLanguageTag, valuePlaceholder: '' })
        return <div key={`fund-detail-porfolio-managers-name-${managerIndex}`}>{manangerNameZh || manangerNameEn || '-'}</div>
      }

      if (type === 'startDate') {
        const { startDate } = managerInfo
        const startDataElement = (
          <div key={`fund-detail-porfolio-managers-start-date-${managerIndex}`}>
            {formatDate(getSpecificAttrValue({ originData: startDate }))}
          </div>
        )
        return startDataElement
      }

      return []
    })

    return managersInfo
  }
  const fundInfoDataSource = [
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.currency' }),
      value: getSpecificAttrValue({ originData: fundDetailOverviewData.baseCurrency.data, attrValue: tcLanguageTag })
    },
    {
      type: `${intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundAUM' })}(${formatDate(
        getSpecificAttrValue({ originData: fundDetailOverviewData.aum, dataPath: 'aumDate' })
      )})`,
      value: `${
        aumAmount !== '-'
          ? `${intl.formatNumber(aumAmount / 1000000, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}(${intl.formatMessage({
              id: 'fundDetail.tabs.overviewTab.fundInfoTable.million'
            })}${getSpecificAttrValue({
              originData: fundDetailOverviewData.baseCurrency.data,
              attrValue: tcLanguageTag
            })})`
          : aumAmount
      }`
    },
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundLaunchDate' }),
      value: formatDate(getSpecificAttrValue({ originData: fundDetailOverviewData.shareclassLaunchDate }))
    },
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundManagers' }),
      value: formatManagersList(fundDetailOverviewData.managerList, 'fullName')
    },
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundManagersStartDate' }),
      value: formatManagersList(fundDetailOverviewData.managerList, 'startDate')
    },
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.morningStarCategory' }),
      value: getSpecificAttrValue({ originData: fundDetailOverviewData.morningstarCategory.data, attrValue: tcLanguageTag })
    },
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.annualManagementFee' }),
      value: parseFloatWithFixed({
        value: fundDetailOverviewData.amf,
        fractionDigits: 2,
        suffix: '%'
      })
    },
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.compareIndex' }),
      value: fundDetailOverviewData.localIndexName || '-'
    },
    {
      type: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.riskRating' }),
      value: getSpecificAttrValue({ originData: fundDetailOverviewData.fundRating })
    }
  ]

  const formatVolatilityRisk = () => {
    const { volatilityRisk } = fundDetailOverviewData
    const defaultVolatilityRisks = [
      { riskType: 'BETA', riskValueY3: null },
      { riskType: 'SHP', riskValueY3: null },
      { riskType: 'STD', riskValueY3: null }
    ]

    const volatilityRiskSortTarget = ['STD', 'BETA', 'SHP']
    const sortedVolatilityRiskData = sortBySpecificArray(
      _.uniqBy([...(volatilityRisk ? volatilityRisk.data : []), ...defaultVolatilityRisks], 'riskType'),
      volatilityRiskSortTarget,
      'riskType'
    )
    const formattedVolatilityRisk = sortedVolatilityRiskData.map((dataItem) => {
      const volatilityRiskData = dataItem.riskValueY3 || '-'
      const formattedRiskValueY3 = !_.isNaN(parseFloat(volatilityRiskData))
        ? `${toFixedCustom(volatilityRiskData, 2)}${dataItem.riskType === 'STD' ? '%' : ''}`
        : volatilityRiskData

      return {
        key: intl.formatMessage({ id: `fundDetail.tabs.overviewTab.fundInfoTable.${dataItem.riskType}` }),
        value: formattedRiskValueY3
      }
    })

    return formattedVolatilityRisk
  }

  const overviewData = {
    fundGeneralInfo: {
      investmentGoalTitle: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.investmentGoalTitle' }),
      introductionTitle: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.introductionTitle' })
    },
    fundInfoTable: {
      columns: [
        {
          key: 'fundInfo',
          title: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTitle' }),
          type: 'string',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { type } = data
              return <span>{type}</span>
            }
          },
          className: 'text-left'
        },
        {
          key: '',
          title: '',
          cell: {
            render: (data) => {
              const { value } = data
              return value
            }
          },
          extraClasses: 'text-right'
        }
      ],
      dataSource: fundInfoDataSource
    },
    fundPerformanceTable: {
      columns: [
        {
          key: 'fund_detail_fund_performance',
          title: (
            <div>
              <span className="fil-fund-detail__fund-performance-table-header-tooltip">
                <span className="fil-tooltip-label"> {intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundMetricsTitle' })}</span>
                <Tooltip fontSize="18" alignmen="left" message={intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.toolTipContent' })} />
              </span>
              <span className="fil-fund-detail__fund-performance-table-header-date">
                ({formatDate(getSpecificAttrValue({ originData: fundDetailOverviewData.volatilityRisk, dataPath: 'measureDate' }))})
              </span>
            </div>
          ),
          type: 'string',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { key } = data
              return <span>{key}</span>
            }
          },
          className: 'text-left'
        },
        {
          key: '',
          title: '',
          cell: {
            render: (data) => {
              const { value } = data
              return value
            }
          },
          extraClasses: 'text-right'
        }
      ],
      dataSource: formatVolatilityRisk()
    },
    fundCodeTable: {
      columns: [
        {
          key: 'fund_detail_fund_code',
          title: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundCodeTitle' }),
          type: 'string',
          cell: {
            extraClasses: 'text-left',
            render: (data) => {
              const { key } = data
              return <span>{key}</span>
            }
          },
          className: 'text-left'
        },
        {
          key: '',
          title: '',
          cell: {
            render: (data) => {
              const { value } = data
              return value
            }
          },
          extraClasses: 'text-right'
        }
      ],
      dataSource: [
        {
          key: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.isin' }),
          value: addPlaceholderValue(fundDetailOverviewData.isin)
        },
        {
          key: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.bloombergCode' }),
          value: addPlaceholderValue(fundDetailOverviewData.bloombergCode)
        },
        {
          key: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundCodeTitle' }),
          value: addPlaceholderValue(fundDetailOverviewData.taCode)
        }
      ]
    }
  }

  return overviewData
}

/* eslint-disable react/style-prop-object */
const formatFundDetailDividend = (dividendData) => {
  const intl = getCustomIntl()
  const dividendNumberConfig = { type: 'number', signDisplay: 'auto', fluctuationColor: false }
  const columns = [
    {
      key: 'payoutDate',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.payoutDate' }),
      type: 'number',
      cell: {
        extraClasses: 'text-left',
        render: (data) => {
          const { excludingDate } = data
          return <DateComponent value={excludingDate} customFormat="yyyy-MM-dd" />
        }
      }
    },
    {
      key: 'couponAmount',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.couponAmount' }),
      type: 'number',
      cell: {
        render: (data) => {
          const { dividendAmount } = data
          return <NumberComponent value={dividendAmount || '-'} fractionDigits={4} {...dividendNumberConfig} />
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'nav',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.nav' }),
      type: 'number',
      cell: {
        render: (data) => {
          const { nav } = data
          return <NumberComponent value={nav || '-'} fractionDigits={4} {...dividendNumberConfig} />
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'monthly',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.monthly' }),
      type: 'number',
      cell: {
        render: (data) => {
          const { divRateOfMonth } = data
          return <NumberComponent value={divRateOfMonth || '-'} style="percent" {...dividendNumberConfig} />
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'rateOfReturn',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.rateOfReturn' }),
      type: 'number',
      cell: {
        render: (data) => {
          const { monthlyCumulativeReturn } = data
          return (
            <NumberComponent
              value={monthlyCumulativeReturn ? divideBy100(monthlyCumulativeReturn) : monthlyCumulativeReturn || '-'}
              style="percent"
              {...dividendNumberConfig}
            />
          )
        }
      },
      extraClasses: 'text-right'
    }
  ]

  const { data: datas, document } = dividendData
  const dataSource = []
  if (datas && datas.length > 0) {
    datas.forEach((item) => {
      dataSource.push({
        excludingDate: item.excludingDate,
        dividendAmount: item.dividendAmount,
        nav: item.nav,
        divRateOfMonth: item.divRateOfMonth,
        divRateOfYear: item.divRateOfYear,
        monthlyCumulativeReturn: item.monthlyCumulativeReturn
      })
    })
  }

  const fundDividendRecordsTable = {
    tableData: {
      columns,
      dataSource,
      extraClasses: 'group-with-indent',
      // collapseAll: true,
      groupByOptions: (data) => {
        const groupedData = _(data)
          .groupBy((dataItem) => dataItem.rowData.excludingDate.substring(0, 4))
          .toPairs()
          .sort((a, b) => parseInt(b[0], 10) - parseInt(a[0], 10))
          .map((dataItem) => {
            const targetData = dataItem[1]
            return {
              parentNode: _.first(targetData),
              children: _.tail(targetData),
              expanded: false
            }
          })
          .value()

        return groupedData
      }
    },

    document: getSpecificAttrValue({
      originData: document,
      attrValue: 'zh-TW',
      dataPath: 'types[0].data',
      targetAttr: 'url',
      valuePlaceholder: ''
    })
  }

  return fundDividendRecordsTable
}

const sortDocumentData = (documentData) => {
  const { countryConfig } = getGlobalParams()
  const sortedDocumentData = sortBySpecificArray(documentData.types, countryConfig.documentTabDocOrders, 'data[0].documentName').map(
    (docItem) => ({
      documentName: docItem.data[0].documentName,
      url: docItem.data[0].url
    })
  )

  return sortedDocumentData
}

const formatFundDetailDocument = (documentData) => {
  const intl = getCustomIntl()
  const sortedDocumentData = sortDocumentData(documentData)
  const columns = [
    {
      key: 'document_name',
      title: intl.formatMessage({ id: 'fundDetail.tabs.documentTab.name' }),
      type: 'string',
      cell: {
        extraClasses: 'text-left',
        render: (data) => {
          const { documentName } = data
          return <span>{documentName}</span>
        }
      },
      extraClasses: 'fil-fund-detail__tab-document__first-header'
    },
    {
      key: 'document_download',
      title: intl.formatMessage({ id: 'fundDetail.tabs.documentTab.download' }),
      type: 'String',
      cell: {
        extraClasses: 'text-right fil-fund-detail__document-table-link',
        render: (data) => {
          const { url } = data
          return <ExternalLink url={url} value={intl.formatMessage({ id: 'fundDetail.tabs.documentTab.clickDownload' })} />
        }
      },
      extraClasses: 'fil-fund-detail__tab-document__second-header'
    }
  ]

  const fundDocumentTable = {
    tableData: {
      columns,
      dataSource: sortedDocumentData
    }
  }
  return fundDocumentTable
}

const formatFundDetailESG = (ESGData, isOnshoreFund) => {
  const { esgCharacteristics, esgDistribution, scId, esgMetrics } = ESGData
  const intl = getCustomIntl()

  const sortESGTableData = (esgTableData) => {
    const sortedesgCharacteristics = []
    esgTableData.forEach((item) => {
      if (item.entityType === 'Waci') {
        sortedesgCharacteristics[0] = item
      }
      if (item.entityType === 'sfdrCategory') {
        sortedesgCharacteristics[1] = item
      }
    })
    return sortedesgCharacteristics
  }

  const createTypeFullContent = (item) => {
    let typeFullName = ''
    if (item) {
      switch (item.entityType) {
        case 'Waci':
          typeFullName = (
            <span>
              {intl.formatMessage({ id: 'fundDetail.tabs.esgTab.carbonEmission' })}
              （<DateComponent value={item.effectiveDate} customFormat="yyyy-MM-dd" />）
            </span>
          )
          break
        case 'sfdrCategory':
          typeFullName = intl.formatMessage({ id: 'fundDetail.tabs.esgTab.sfdrCategory' })
          break
        default:
          typeFullName = ''
      }
      return typeFullName
    }

    return ''
  }
  const columns = [
    {
      key: 'type',
      title: '',
      type: 'string',
      cell: {
        extraClasses: 'text-left',
        render: (data) => {
          return createTypeFullContent(data)
        }
      },
      extraClasses: 'fil-fund-detail__tab-esg__first-header'
    },
    {
      key: 'fund',
      title: intl.formatMessage({ id: 'fundDetail.tabs.esgTab.esgFund' }),
      type: 'String',
      cell: {
        extraClasses: 'text-right fil-fund-detail__esg-table',
        render: (data) => {
          const { fundValue } = data
          return fundValue || intl.formatMessage({ id: 'fundDetail.tabs.esgTab.inapplicability' })
        }
      },
      extraClasses: 'text-right fil-fund-detail__tab-esg__second-header'
    },
    ...(!isOnshoreFund
      ? [
          {
            key: 'index',
            title: intl.formatMessage({ id: 'fundDetail.tabs.esgTab.esgIndex' }),
            type: 'String',
            cell: {
              extraClasses: 'text-right fil-fund-detail__esg-table',
              render: (data) => {
                const { indexValue } = data
                return indexValue || intl.formatMessage({ id: 'fundDetail.tabs.esgTab.inapplicability' })
              }
            },
            extraClasses: 'text-right fil-fund-detail__tab-esg__second-header'
          }
        ]
      : [])
  ]

  const fundESGTable = esgCharacteristics.length
    ? {
        tableData: {
          columns,
          dataSource: sortESGTableData(esgCharacteristics)
        }
      }
    : null

  const categories = []
  const fundRatingData = []
  const indexRatingData = []
  let esgChartData = null

  if (esgDistribution) {
    esgDistribution.details.forEach((item) => {
      if (item.ratingType !== 'CO') {
        categories.push(_.find(item.ratingName, (ratingNameItem) => ratingNameItem.languageTag === 'zh-TW').description)
        fundRatingData.push(parseFloat(item.fundValue))

        if (!isOnshoreFund) {
          indexRatingData.push(parseFloat(item.indexValue))
        }
      }
    })

    esgChartData = {
      scId,
      effectiveDate: format(new Date(esgDistribution.effectiveDate), 'yyyy-MM-dd'),
      categories,
      chartData: [
        {
          name: intl.formatMessage({ id: 'fundDetail.tabs.esgTab.esgFund' }),
          data: fundRatingData
        },
        ...(indexRatingData.length
          ? [
              {
                name: intl.formatMessage({ id: 'fundDetail.tabs.esgTab.esgIndex' }),
                data: indexRatingData
              }
            ]
          : [])
      ]
    }
  }

  return { fundESGTable, esgChartData, esgMetrics }
}

const formatAnnualReturnData = (originData) => {
  const intl = getCustomIntl()

  const sortedDetails = originData.details.sort((a, b) => parseInt(a.year, 10) - parseInt(b.year, 10))
  let acceptableDetails = []

  if (sortedDetails.length >= ANNUAL_RETURE_YEARS) {
    acceptableDetails = _.takeRight(sortedDetails, ANNUAL_RETURE_YEARS)
  } else if (sortedDetails.length) {
    const latestYear = parseInt(_.last(sortedDetails).year, 10)
    const yearRange = _.range(latestYear - ANNUAL_RETURE_YEARS + 1, latestYear + 1).map((year) => ({
      year: year.toString(),
      fundReturn: null,
      categoryReturn: null
    }))

    acceptableDetails = _.uniqBy([...sortedDetails, ...yearRange], 'year')
  } else {
    const currentYear = parseInt(new Date().getFullYear(), 10)
    const yearRange = _.range(currentYear - ANNUAL_RETURE_YEARS, currentYear).map((year) => ({
      year: year.toString(),
      fundReturn: null,
      categoryReturn: null
    }))
    acceptableDetails = _.uniqBy([...sortedDetails, ...yearRange], 'year')
  }

  const sortedAcceptableDetails = acceptableDetails.sort((a, b) => parseInt(a.year, 10) - parseInt(b.year, 10))

  const columns = [
    {
      key: 'annualTitle',
      title: '',
      dataIndex: 'title',
      type: 'string',
      cell: {
        extraClasses: 'text-left',
        render: (data) => <span>{data}</span>
      },
      extraClasses: 'text-left'
    },
    ...sortedAcceptableDetails.map((item) => {
      return {
        key: item.year,
        title: item.year,
        dataIndex: item.year,
        type: 'string',
        cell: {
          extraClasses: 'text-right',
          render: (data) => <NumberComponent {...{ value: data ? divideBy100(data) : data || '-', ...numberConfig }} />
        },
        extraClasses: 'text-right'
      }
    })
  ]

  const dataSource = [
    _.reduce(
      sortedAcceptableDetails,
      (prevItem, currentItem) => {
        const { year, fundReturn } = currentItem
        return {
          ...prevItem,
          [year]: fundReturn
        }
      },
      { title: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.annualReturnTable.currentFund' }) }
    ),
    _.reduce(
      sortedAcceptableDetails,
      (prevItem, currentItem) => {
        const { year, categoryReturn } = currentItem
        return {
          ...prevItem,
          [year]: categoryReturn
        }
      },
      { title: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.annualReturnTable.categoryReturn' }) }
    )
  ]

  return {
    columns,
    dataSource
  }
}

const formatCumulativeReturnData = (originData) => {
  const intl = getCustomIntl()
  const dataOrder = [
    { key: 'cumulativeReturnYTD', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.YTD' }) },
    { key: 'cumulativeReturnM3', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.monthsText' }, { monthsCount: 3 }) },
    { key: 'cumulativeReturnM6', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.monthsText' }, { monthsCount: 6 }) },
    { key: 'cumulativeReturnY1', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 1 }) },
    { key: 'cumulativeReturnY2', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 2 }) },
    { key: 'cumulativeReturnY3', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 3 }) },
    { key: 'cumulativeReturnY5', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.yearsText' }, { yearsCount: 5 }) },
    { key: 'cumulativeReturnMax', value: intl.formatMessage({ id: 'fundDetail.tabs.performanceTab.cumulativeReturn.sinceLaunch' }) }
  ]

  const { details, returnDate } = originData

  const defaultDetails =
    details.map((item) => ({
      ...item,
      title: intl.formatMessage({
        id: `fundDetail.tabs.performanceTab.annualReturnTable.${item.entityType === 'SC' ? 'currentFund' : 'categoryReturn'}`
      })
    })) || []

  const columns = [
    {
      key: 'cumulativeTitle',
      title: '',
      dataIndex: 'title',
      type: 'string',
      cell: {
        extraClasses: 'text-left',
        render: (data) => <span>{data}</span>
      },
      extraClasses: 'text-left'
    },
    ...dataOrder.map(({ key, value }) => {
      return {
        key,
        title: value,
        dataIndex: key,
        type: 'String',
        cell: {
          extraClasses: 'text-right',
          render: (data) => <NumberComponent {...{ value: data ? divideBy100(data) : data || '-', ...numberConfig }} />
        },
        extraClasses: 'text-right'
      }
    })
  ]

  return {
    returnDate: returnDate ? format(new Date(returnDate).getTime(), 'yyyy-MM-dd') : '-',
    tableData: {
      columns,
      dataSource: defaultDetails
    }
  }
}

// onshore offshore
const convertFundRange = (fundItem, rule, terms) => {
  const isOnShore = fundItem.fundRange && fundItem.fundRange.rangeCode === 'TW-ONSHORE'
  const $fundRangeCode = isOnShore ? fillTerms(`#{fundList.map.onShore}`, terms) : fillTerms('#{fundList.map.offShore}', terms)

  const $fundRangeCodeShort = isOnShore
    ? fillTerms(`#{fundList.map.onShoreShort}`, terms)
    : fillTerms('#{fundList.map.offShoreShort}', terms)

  const $fundRangeCodeValue = isOnShore ? 'TW-ONSHORE' : 'TW-OFFSHORE'
  return Object.assign(fundItem, { $fundRangeCode, $fundRangeCodeShort, $fundRangeCodeValue })
}

const formatDividendAloneTableData = (dividendData) => {
  const intl = getCustomIntl()
  const { baseUrl } = getGlobalParams()
  const dividendNumberConfig = { type: 'number', signDisplay: 'auto', fluctuationColor: false }

  const columns = [
    {
      key: 'level',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.level' }),
      type: 'string',
      cell: {
        extraClasses: 'text-left',
        render: (data) => {
          const { category } = data
          return <span>{category}</span>
        }
      },
      extraClasses: 'fil-fund-dividend-records__tables-list-item-table__first-header'
    },
    {
      key: 'payoutDate',
      title: [intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.payoutDate' }).split(' ')],
      type: 'string',
      cell: {
        render: (data) => {
          const { excludingDate } = data
          return <DateComponent value={excludingDate} customFormat="yyyy-MM-dd" />
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'couponAmount',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.couponAmount' }),
      type: 'string',
      cell: {
        render: (data) => {
          const { dividendAmount } = data
          return <NumberComponent value={dividendAmount || '-'} fractionDigits={4} {...dividendNumberConfig} />
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'nav',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.nav' }),
      type: 'string',
      cell: {
        render: (data) => {
          const { nav } = data
          return <NumberComponent value={nav || '-'} fractionDigits={4} {...dividendNumberConfig} />
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'monthly',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.monthly' }),
      type: 'string',
      cell: {
        render: (data) => {
          const { divRateOfMonth } = data
          return <NumberComponent value={divRateOfMonth || '-'} style="percent" {...dividendNumberConfig} />
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'rateOfReturn',
      title: intl
        .formatMessage({ id: 'fundDetail.tabs.dividendTab.rateOfReturn' })
        .split(' ')
        .map((item) => {
          return <div key={item}>{item}</div>
        }),
      type: 'string',
      cell: {
        render: (data) => {
          const { monthlyCumulativeReturn } = data
          return (
            <NumberComponent
              value={monthlyCumulativeReturn ? divideBy100(monthlyCumulativeReturn) : monthlyCumulativeReturn || '-'}
              style="percent"
              {...dividendNumberConfig}
            />
          )
        }
      },
      extraClasses: 'text-right'
    },
    {
      key: 'fundDetail',
      title: intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.fundDetail' }),
      // dataIndex: 'shareClassName.zh', // if no dataIndex, will pass each data to render
      type: 'string',
      cell: {
        extraClasses: 'fil-fund-dividend-records__tables-list-item-table__button',
        render: (data) => {
          const { fundDetail } = data

          return fundDetail ? (
            <Button
              url={`${baseUrl}/funds/${replaceSpecialChars(fundDetail)}`}
              label={intl.formatMessage({ id: 'fundDetail.tabs.dividendTab.fundDetail' })}
              target="_self"
              kind="secondary"
              size="small"
            />
          ) : (
            ''
          )
        }
      },
      extraClasses: 'fil-fund-dividend-records__tables-list-item-table__button'
    }
  ]

  const sortDividendData = (a, b) =>
    replaceSpecialChars(a.fundName.filter((name) => name.languageTag === 'en-001')[0].name).localeCompare(
      replaceSpecialChars(b.fundName.filter((name) => name.languageTag === 'en-001')[0].name)
    )

  const sortGroupData = (a, b) => replaceSpecialChars(b.excludingDate).localeCompare(replaceSpecialChars(a.excludingDate))

  const formattedData = dividendData.sort(sortDividendData).map(({ fundId, fundName, groups, shareclassNote, fundOriginalName }) => {
    const fundNameZh = getSpecificAttrValue({ originData: fundName, attrValue: 'zh-TW', valuePlaceholder: '' })
    const fundNameEn = getSpecificAttrValue({ originData: fundName, attrValue: 'en-001' })
    const fundOriginalNameZh = getSpecificAttrValue({ originData: fundOriginalName, attrValue: 'zh-TW', valuePlaceholder: '' })
    const fundOriginalNameEn = getSpecificAttrValue({ originData: fundOriginalName, attrValue: 'en-001', valuePlaceholder: '' })
    const finalFundOriginalName = fundOriginalNameZh || fundOriginalNameEn || ''

    const fullFundName = `${fundNameZh || fundNameEn || '-'}${finalFundOriginalName ? `(${finalFundOriginalName})` : ''}`

    const dataSource = []
    /* eslint-disable */
    groups.map((groupsItem) => {
      groupsItem.data.sort(sortGroupData).map((item, index) =>
        dataSource.push({
          scId: groupsItem.scId,
          index,
          category: index === 0 ? getSpecificAttrValue({ originData: groupsItem.classOfShares, attrValue: 'zh-TW' }) : '',
          excludingDate: item.excludingDate,
          dividendAmount: item.dividendAmount,
          nav: item.nav,
          divRateOfMonth: item.divRateOfMonth,
          divRateOfYear: item.divRateOfYear,
          monthlyCumulativeReturn: item.monthlyCumulativeReturn,
          fundDetail: index === 0 ? getSpecificAttrValue({ originData: groupsItem.shareclassName, attrValue: 'en-001' }) : ''
        })
      )
    })

    return {
      tableData: {
        columns,
        dataSource,
        extraClasses: 'group-with-indent',
        // collapseAll: true,
        groupByOptions: (data) => {
          const groupedData = _(data)
            .groupBy((dataItem) => dataItem.rowData.scId)
            .toPairs()
            .map((dataItem) => {
              const targetData = dataItem[1]
              return {
                parentNode: _.first(targetData),
                children: _.tail(targetData),
                expanded: false
              }
            })
            .value()

          return groupedData
        }
      },
      fundId,
      fundName: fullFundName,
      description: shareclassNote
    }
  })
  return formattedData
}

const formatFundDetailHoldings = (holdingsData, holdingsType, assetClassId, isGFSTX) => {
  const intl = getCustomIntl()

  const shouldRemoveHoldingsMapping = {
    type1: ['ISSUER'],
    type2: ['creditAllocation', 'currencyAllocation', 'ISSUE'],
    type3: ['countryAllocation', 'sectorAllocation', 'creditAllocation', 'currencyAllocation', 'ISSUE']
  }

  /* eslint-disable no-param-reassign */
  shouldRemoveHoldingsMapping[holdingsType].forEach((item) => {
    if (item !== 'ISSUE' && item !== 'ISSUER') {
      holdingsData[item] = null
    } else {
      holdingsData.holdings.forEach((val, index) => {
        if (val.holdingLevel === item) {
          holdingsData.holdings.splice(index, 1)
        }
      })
    }
  })

  const sortHoldingsBody = (holdingsBody, compareField = 'orderId', topNumber) => {
    const sortedHoldingsBody = holdingsBody.sort((a, b) => parseInt(a[compareField], 10) - parseInt(b[compareField], 10))
    if (topNumber) {
      sortedHoldingsBody.splice(parseInt(topNumber, 10))
    }
    return sortedHoldingsBody
  }

  const generateColorBarValue = (maxFundWeighting, currentFundWeighting) => {
    const colorBarValue = parseFloat(toFixedCustom(currentFundWeighting, 2)) / parseFloat(toFixedCustom(maxFundWeighting, 2))
    return String(Math.abs(colorBarValue) * 100)
  }

  const filterItemIfNotRequired = (data) => {
    const filteredItem = []
    if (!isGFSTX) {
      data.forEach((item) => {
        if (item.fundWeighting && parseFloat(item.fundWeighting) !== 0) {
          filteredItem.push(item)
        }
      })
    } else {
      data.forEach((item) => {
        if (item.indexWeighting && parseFloat(item.indexWeighting) !== 0) {
          filteredItem.push(item)
        }
      })
    }
    return filteredItem
  }

  const formatHoldingsBody = (holdingsBody, issuerType2or3) => {
    const weightingArray = []
    holdingsBody.forEach((item) => {
      weightingArray.push(isGFSTX ? Math.abs(item.indexWeighting) || 0 : Math.abs(item.fundWeighting) || 0)
    })
    const maxFundWeighting = weightingArray.sort((a, b) => parseFloat(a) - parseFloat(b))[weightingArray.length - 1]
    return holdingsBody.map((item) => ({
      cells: [
        [
          {
            value:
              getSpecificAttrValue({ originData: item.breakdownName, attrValue: tcLanguageTag, valuePlaceholder: '' }) ||
              getSpecificAttrValue({ originData: item.breakdownName, attrValue: enLanguageTag, valuePlaceholder: '' }) ||
              getSpecificAttrValue({ originData: item.securitiesName, valuePlaceholder: '' }) ||
              '-',
            type: 'label'
          }
        ],
        ...(issuerType2or3
          ? [
              [
                {
                  value:
                    getSpecificAttrValue({ originData: item.sector, attrValue: tcLanguageTag, valuePlaceholder: '' }) ||
                    getSpecificAttrValue({ originData: item.sector, attrValue: enLanguageTag, valuePlaceholder: '' }) ||
                    '-',
                  type: 'label',
                  extraClasses: 'fil-fund-detail__portfolio-table__columen-sector'
                }
              ]
            ]
          : []),
        [
          {
            value: 'ColorBar',
            type: 'customElement',
            props: {
              label: parseFloatWithFixed({
                value: !isGFSTX ? item.fundWeighting : item.indexWeighting,
                fractionDigits: 2,
                suffix: '%'
              }),
              index: !isGFSTX ? (parseFloat(item.fundWeighting) >= 0 ? 1 : 14) : parseFloat(item.indexWeighting) >= 0 ? 1 : 14,
              progress: parseFloatWithFixed({
                value: generateColorBarValue(maxFundWeighting, !isGFSTX ? item.fundWeighting : item.indexWeighting),
                fractionDigits: 2,
                suffix: '%'
              }),
              isVertical: issuerType2or3
            }
          }
        ]
      ],
      extraClasses: 'fil-fund-detail__portfolio-table-colorbar'
    }))
  }

  const fundDetailPortfolioTabInfo = []

  if (holdingsData.countryAllocation) {
    fundDetailPortfolioTabInfo.push({
      key: 'REGION',
      title: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.countryAllocation' }),
      date: holdingsData.countryAllocation.date,
      tableData: {
        headers: [
          {
            key: 'REGION',
            value: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.region' }),
            type: 'String',
            extraClasses: ''
          },
          {
            key: 'FUND',
            value: !isGFSTX
              ? intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.fund' })
              : intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.index' }),
            type: 'String',
            extraClasses: ''
          }
        ],
        body: formatHoldingsBody(sortHoldingsBody(filterItemIfNotRequired(holdingsData.countryAllocation.data), 'orderId'))
      },
      needInitSort: false,
      tableDisclaimer: intl.formatMessage({
        id: `fundDetail.tabs.holdingsTab.countryAllocationDisclaimer_${assetClassId}`
      })
    })
  }

  if (holdingsData.sectorAllocation) {
    fundDetailPortfolioTabInfo.push({
      key: 'INDUSTRY',
      title: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.sectorAllocation' }),
      date: holdingsData.sectorAllocation.date,
      tableData: {
        headers: [
          {
            key: 'INDUSTRY',
            value: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.industry' }),
            type: 'String',
            extraClasses: ''
          },
          {
            key: 'FUND',
            value: !isGFSTX
              ? intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.fund' })
              : intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.index' }),
            type: 'String',
            extraClasses: ''
          }
        ],
        body: formatHoldingsBody(sortHoldingsBody(filterItemIfNotRequired(holdingsData.sectorAllocation.data), 'orderId'))
      },
      needInitSort: false,
      tableDisclaimer: intl.formatMessage({ id: `fundDetail.tabs.holdingsTab.sectorAllocationDisclaimer_${assetClassId}` })
    })
  }

  holdingsData.holdings.forEach((holding) => {
    if (holding.holdingLevel === 'ISSUE') {
      fundDetailPortfolioTabInfo.push({
        key: 'DEBTHOLDINGS',
        title: intl.formatMessage({ id: `fundDetail.tabs.holdingsTab.holdingsTopTen_${assetClassId}` }),
        date: holding.topHoldingDate,
        tableData: {
          headers: [
            {
              key: 'DEBTHOLDINGS',
              value: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.debtholdings' }),
              type: 'String',
              extraClasses: ''
            },
            {
              key: 'FUND',
              value: !isGFSTX
                ? intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.fund' })
                : intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.index' }),
              type: 'String',
              extraClasses: ''
            }
          ],
          body: formatHoldingsBody(sortHoldingsBody(filterItemIfNotRequired(holding.data), 'orderId', 10))
        },
        needInitSort: false,
        tableDisclaimer: intl.formatMessage({ id: `fundDetail.tabs.holdingsTab.holdingsTopTenDisclaimer_${assetClassId}` })
      })
    }
  })

  if (holdingsData.creditAllocation) {
    fundDetailPortfolioTabInfo.push({
      key: 'CREDITRATING',
      title: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.creditAllocation' }),
      date: holdingsData.creditAllocation.date,
      tableData: {
        headers: [
          {
            key: 'CREDITRATING',
            value: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.creditrating' }),
            type: 'String',
            extraClasses: ''
          },
          {
            key: 'FUND',
            value: !isGFSTX
              ? intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.fund' })
              : intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.index' }),
            type: 'String',
            extraClasses: ''
          }
        ],
        body: formatHoldingsBody(sortHoldingsBody(filterItemIfNotRequired(holdingsData.creditAllocation.data), 'orderId'))
      },
      needInitSort: false,
      tableDisclaimer: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.creditAllocationDisclaimer' })
    })
  }

  if (holdingsData.currencyAllocation) {
    fundDetailPortfolioTabInfo.push({
      key: 'CURRENCY',
      title: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.currencyAllocation' }),
      date: holdingsData.currencyAllocation.date,
      tableData: {
        headers: [
          {
            key: 'CURRENCY',
            value: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.currency' }),
            type: 'String',
            extraClasses: ''
          },
          {
            key: 'FUND',
            value: !isGFSTX
              ? intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.fund' })
              : intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.index' }),
            type: 'String',
            extraClasses: ''
          }
        ],
        body: formatHoldingsBody(sortHoldingsBody(filterItemIfNotRequired(holdingsData.currencyAllocation.data), 'orderId'))
      },
      needInitSort: false,
      tableDisclaimer: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.currencyAllocationDisclaimer' })
    })
  }

  holdingsData.holdings.forEach((holding) => {
    if (holding.holdingLevel === 'ISSUER') {
      let hasSector = false
      holding.data.forEach((item) => {
        if (item.sector.length > 0) {
          hasSector = true
        }
      })
      fundDetailPortfolioTabInfo.push({
        key: 'DEBTISSUERS',
        title: intl.formatMessage({
          id: `fundDetail.tabs.holdingsTab.${holdingsType === 'type1' ? 'holdingsTopFive' : 'holdingsTopTen_FEF'}`
        }),
        date: holding.topHoldingDate,
        tableData: {
          headers: [
            {
              key: 'DEBTISSUERS',
              value: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.debtissuers' }),
              type: 'String',
              extraClasses: ''
            },
            ...(holdingsType !== 'type1' && hasSector
              ? [
                  {
                    key: 'SECTOR',
                    value: intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.industry' }),
                    type: 'String',
                    extraClasses: ''
                  }
                ]
              : []),
            {
              key: 'FUND',
              value: !isGFSTX
                ? intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.fund' })
                : intl.formatMessage({ id: 'fundDetail.tabs.holdingsTab.index' }),
              type: 'String',
              extraClasses: holdingsType !== 'type1' && hasSector ? 'text-right' : ''
            }
          ],
          body: formatHoldingsBody(
            sortHoldingsBody(filterItemIfNotRequired(holding.data), 'orderId', holdingsType === 'type1' ? 5 : 10),
            holdingsType !== 'type1' && hasSector
          )
        },
        needInitSort: false,
        tableDisclaimer: intl.formatMessage({
          id: `fundDetail.tabs.holdingsTab.${holdingsType === 'type1' ? 'holdingsTopFiveDisclaimer' : 'holdingsTopTenDisclaimer_FEF'}`
        })
      })
    }
  })

  return fundDetailPortfolioTabInfo
}

const formatDividendDocuments = (documents) => {
  if (Array.isArray(documents) && documents.length > 0) {
    const allDocs = documents.map((doc) => {
      const item = doc.types[0].data[0]
      return {
        rangeCode: doc.rangeCode,
        title: item.documentName,
        href: item.url,
        icon: 'office-file-pdf-1'
      }
    })

    return sortBySpecificArray(allDocs, ['TW-ONSHORE', 'FF'], 'rangeCode')
  }
  return []
}

const formatComparisonData = (data) => {
  const fundsData = data
  if (Array.isArray(fundsData) && fundsData.length > 0) {
    const intl = getCustomIntl()
    // detailData
    const getManagerName = (managerList) => {
      const managerNameArray = []
      if (managerList && managerList.length > 0) {
        managerList.forEach((item) => {
          managerNameArray.push({
            value:
              getSpecificAttrValue({ originData: item.fullName, attrValue: tcLanguageTag, valuePlaceholder: '' }) ||
              getSpecificAttrValue({ originData: item.fullName, attrValue: enLanguageTag, valuePlaceholder: '' }) ||
              '-',
            type: 'label',
            extraClasses: 'fil-fund-compare__table-component__item'
          })
        })
        return managerNameArray
      }
      return '-'
    }

    const getManagerStartDate = (managerList) => {
      const managerStartDateArray = []
      if (managerList && managerList.length > 0) {
        managerList.forEach((item) => {
          managerStartDateArray.push({
            value: formatDate(getSpecificAttrValue({ originData: item.startDate, attrValue: tcLanguageTag, valuePlaceholder: '' })) || '-',
            type: 'label',
            extraClasses: 'fil-fund-compare__table-component__item'
          })
        })
        return managerStartDateArray
      }
      return '-'
    }

    const detailBody = [
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.currency' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundAUM' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundLaunchDate' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundManagers' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundManagersStartDate' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.annualManagementFee' }),
              type: 'label'
            }
          ]
        ]
      }
    ]

    fundsData.forEach((fund) => {
      const { responseData } = fund
      const { formattedComparisonData: fundItem } = responseData
      if (fundItem) {
        detailBody[0].cells.push([
          {
            value: getSpecificAttrValue({ originData: fundItem.baseCurrency.data, attrValue: tcLanguageTag }),
            type: 'label'
          }
        ])
        if (!fundItem.aum) {
          detailBody[1].cells.push([
            {
              value: '-',
              type: 'label',
              extraClasses: 'fil-fund-compare__table-component__item'
            }
          ])
        } else {
          detailBody[1].cells.push([
            {
              value:
                fundItem.aum.fundAum !== '-'
                  ? intl.formatNumber(fundItem.aum.fundAum / 1000000, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })
                  : fundItem.aum.fundAum,
              type: 'label',
              extraClasses: 'fil-fund-compare__table-component__item'
            },
            {
              value: `(${intl.formatMessage({
                id: 'fundDetail.tabs.overviewTab.fundInfoTable.million'
              })}${getSpecificAttrValue({
                originData: fundItem.baseCurrency.data,
                attrValue: tcLanguageTag
              })})`,
              type: 'label',
              extraClasses: 'fil-fund-compare__table-component__item'
            },
            {
              value: formatDate(getSpecificAttrValue({ originData: fundItem.aum.aumDate })),
              type: 'label',
              extraClasses: 'fil-fund-compare__table-component__item'
            }
          ])
        }
        detailBody[2].cells.push([
          { value: formatDate(getSpecificAttrValue({ originData: fundItem.shareclassLaunchDate })), type: 'label' }
        ])

        detailBody[3].cells.push(getManagerName(fundItem.managerList))
        detailBody[4].cells.push(getManagerStartDate(fundItem.managerList))
        detailBody[5].cells.push([
          {
            value: parseFloatWithFixed({
              value: fundItem.amf,
              fractionDigits: 2,
              suffix: '%'
            }),
            type: 'label'
          }
        ])
      }
    })

    // metricsData
    const metricsBody = [
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.STD' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.BETA' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.SHP' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.MeasureDate' }),
              type: 'label'
            }
          ]
        ]
      }
    ]

    const getVolatilityRisk = (risk, type) => {
      let formattedRiskValueY3 = '-'
      if (risk && risk.data && Array.isArray(risk.data)) {
        risk.data.forEach((item) => {
          if (item.riskType === type) {
            const volatilityRiskData = item.riskValueY3 || '-'
            formattedRiskValueY3 = !_.isNaN(parseFloat(volatilityRiskData))
              ? `${toFixedCustom(volatilityRiskData, 2)}${item.riskType === 'STD' ? '%' : ''}`
              : volatilityRiskData
          }
        })
      }
      return formattedRiskValueY3
    }

    fundsData.forEach((fund) => {
      const { responseData } = fund
      const { formattedComparisonData: fundItem } = responseData
      if (fundItem) {
        metricsBody[0].cells.push([
          {
            value: getVolatilityRisk(fundItem.volatilityRisk, 'STD'),
            type: 'label'
          }
        ])
        metricsBody[1].cells.push([{ value: getVolatilityRisk(fundItem.volatilityRisk, 'BETA'), type: 'label' }])
        metricsBody[2].cells.push([
          {
            value: getVolatilityRisk(fundItem.volatilityRisk, 'SHP'),
            type: 'label'
          }
        ])
        metricsBody[3].cells.push([
          { value: formatDate(getSpecificAttrValue({ originData: fundItem.volatilityRisk, dataPath: 'measureDate' })), type: 'label' }
        ])
      }
    })

    // ratingData
    const ratingBody = [
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.isin' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.bloombergCode' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.fundCode' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.riskLevel' }),
              type: 'label'
            }
          ]
        ]
      },
      {
        cells: [
          [
            {
              value: intl.formatMessage({ id: 'fundDetail.tabs.overviewTab.fundInfoTable.morningStarRating' }),
              type: 'label'
            }
          ]
        ]
      }
    ]

    fundsData.forEach((fund) => {
      const { responseData } = fund
      const { formattedComparisonData: fundItem } = responseData
      if (fundItem) {
        ratingBody[0].cells.push([
          {
            value: getSpecificAttrValue({ originData: fundItem.isin }),
            type: 'label'
          }
        ])
        ratingBody[1].cells.push([{ value: getSpecificAttrValue({ originData: fundItem.bloombergCode }), type: 'label' }])
        ratingBody[2].cells.push([
          {
            value: getSpecificAttrValue({ originData: fundItem.taCode }),
            type: 'label'
          }
        ])
        ratingBody[3].cells.push([{ value: getSpecificAttrValue({ originData: fundItem.fundRating }), type: 'label' }])
        if (fundItem.msRating) {
          ratingBody[4].cells.push([
            {
              value: 'MorningStar',
              type: 'customElement',
              props: { starnum: fundItem.msRating, starSize: 12, extraClasses: 'fil-fund-compare__table-component__ms' }
            }
          ])
        } else {
          ratingBody[4].cells.push([
            {
              value: intl.formatMessage({ id: 'nonRiskRating' }),
              type: 'label'
            }
          ])
        }
      }
    })

    return {
      detailData: {
        tableData: {
          body: detailBody
        }
      },
      metricsData: {
        tableData: {
          body: metricsBody
        }
      },
      ratingData: {
        tableData: {
          body: ratingBody
        }
      }
    }
  }

  return {
    detailData: {
      tableData: {
        body: []
      }
    },
    metricsData: {
      tableData: {
        body: []
      }
    },
    ratingData: {
      tableData: {
        body: []
      }
    }
  }
}

const formatComparisonPerformanceData = (data) => {
  const fundsData = data
  if (Array.isArray(fundsData) && fundsData.length > 0) {
    // rateOfReturn
    const annualReturnBody = []
    const tempAnnualReturnHeader = []

    const currentYear = parseInt(new Date().getFullYear(), 10)
    for (let i = 1; i <= 5; i++) {
      tempAnnualReturnHeader.push(String(currentYear - i))
    }

    tempAnnualReturnHeader.forEach((item) => {
      annualReturnBody.push({
        cells: [
          [
            {
              value: item,
              type: 'label'
            }
          ]
        ]
      })
    })

    fundsData.forEach((fund) => {
      const { responseData } = fund
      const { formattedComparisonPerformanceData: fundItem } = responseData
      if (fundItem) {
        annualReturnBody.forEach((bodyItem) => {
          let hasValueFlag = false
          fundItem.details.forEach((annualReturnItem) => {
            if (bodyItem.cells[0][0].value === annualReturnItem.year) {
              bodyItem.cells.push([
                {
                  value: annualReturnItem.fundReturn ? divideBy100(annualReturnItem.fundReturn) : annualReturnItem.fundReturn || '-',
                  type: 'number',
                  ...numberConfig
                }
              ])

              hasValueFlag = true
            }
          })
          if (!hasValueFlag) {
            bodyItem.cells.push([
              {
                value: '-',
                type: 'label'
              }
            ])
          }
        })
      }
    })
    return {
      rateOfReturnData: {
        tableData: {
          body: annualReturnBody
        }
      }
    }
  }

  return {
    rateOfReturnData: {
      tableData: {
        body: []
      }
    }
  }
}

const formatComparisonChartData = (performanceChartData, chartConfig) => {
  const performanceChartConfig = {
    ...chartConfig,
    series: _.reduce(performanceChartData, (previousChartData, nextChartData) => [...previousChartData, ...nextChartData.series], [])
  }

  return performanceChartConfig
}

const convertTokenizers = ({ tokenizers } = {}) => {
  const wordsSet = new Set()
  if (Array.isArray(tokenizers) && tokenizers.length > 0) {
    tokenizers.forEach((tokenizer) => {
      tokenizer
        .split(' ')
        .filter((item) => !!item.trim())
        .forEach((item) => wordsSet.add(item))
    })
    return Array.from(wordsSet.values())
  }
  return []
}

export {
  formatFundDetailOverview,
  formatFundDetailDividend,
  formatFundDetailDocument,
  convertFundRange,
  formatAnnualReturnData,
  formatCumulativeReturnData,
  formatDividendAloneTableData,
  formatFundDetailHoldings,
  formatDividendDocuments,
  formatComparisonData,
  formatComparisonPerformanceData,
  formatComparisonChartData,
  formatFundDetailESG,
  convertTokenizers
}
